import { useTranslation } from 'react-i18next';
import { card, cardBody, REG_FONTS, flagBar } from './styles';
import type { EvaluationResult } from './evaluator';

interface QuizResultsProps {
  result: EvaluationResult;
}

interface ResultCardProps {
  title: string;
  items: string[];
  borderColor: string;
  bgColor: string;
}

function ResultCard({ title, items, borderColor, bgColor }: ResultCardProps) {
  if (items.length === 0) return null;

  return (
    <div
      className="rounded-xl p-4 mb-3"
      style={{ borderLeft: `4px solid ${borderColor}`, backgroundColor: bgColor }}
    >
      <p className="font-bold text-sm text-[#1a1a1a] mb-2" style={{ fontFamily: REG_FONTS.body }}>
        {title}
      </p>
      <ul className="list-disc list-inside space-y-1">
        {items.map((item, i) => (
          <li key={i} className="text-sm text-[#444]" style={{ fontFamily: REG_FONTS.body }}>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function QuizResults({ result }: QuizResultsProps) {
  const { t } = useTranslation('regularizacion');

  return (
    <div className={card}>
      {flagBar}
      <div className={cardBody}>
        {/* Main result */}
        <div
          className={`rounded-xl p-5 mb-5 text-center ${
            result.eligible
              ? 'border-2 border-[#2e7d32] bg-[#e8f5e9]'
              : 'border-2 border-[#c62828] bg-[#ffebee]'
          }`}
        >
          <p
            className={`text-xl font-bold mb-2 ${
              result.eligible ? 'text-[#2e7d32]' : 'text-[#c62828]'
            }`}
            style={{ fontFamily: REG_FONTS.heading }}
          >
            {result.eligible ? t('results.eligible') : t('results.notEligible')}
          </p>
          <p className="text-sm text-[#444]" style={{ fontFamily: REG_FONTS.body }}>
            {result.eligible ? t('results.eligibleDesc') : t('results.notEligibleDesc')}
          </p>
        </div>

        {/* Detail cards */}
        <ResultCard
          title={t('results.issues')}
          items={result.issues}
          borderColor="#c62828"
          bgColor="#ffebee"
        />
        <ResultCard
          title={t('results.warnings')}
          items={result.warnings}
          borderColor="#ef6c00"
          bgColor="#fff3e0"
        />
        <ResultCard
          title={t('results.positives')}
          items={result.positives}
          borderColor="#2e7d32"
          bgColor="#e8f5e9"
        />
        <ResultCard
          title={t('results.family')}
          items={result.familyNotes}
          borderColor="#1565c0"
          bgColor="#e3f2fd"
        />
        <ResultCard
          title={t('results.practical')}
          items={result.practicalInfo}
          borderColor="#6a1b9a"
          bgColor="#f3e5f5"
        />

        {/* Disclaimer */}
        <div className="rounded-xl p-4 mt-4 bg-[#f9f9f9] border border-[#ef6c00]/30">
          <p className="text-xs text-[#666] italic" style={{ fontFamily: REG_FONTS.body }}>
            {t('results.disclaimer')}
          </p>
        </div>
      </div>
    </div>
  );
}
